export type CheckoutQuantities = Record<string, string>;

/** Seeding runs on every order change; an unchanged seed must not produce a new state object. */
export function checkoutQuantitiesEqual(a: CheckoutQuantities, b: CheckoutQuantities): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) {
    return false;
  }
  return aKeys.every((key) => a[key] === b[key]);
}

/**
 * Quantity inputs seeded from an order, clamped to what the buyer may actually order.
 *
 * `needsClampPersist` reports that the clamp disagrees with the stored order, so the caller still
 * has to write it back.
 */
export function resolveCheckoutQuantitySeed(
  lineItems: { id: string; quantity: number }[],
  clampQuantity: (lineItemId: string, quantity: number) => number,
): { quantities: CheckoutQuantities; needsClampPersist: boolean } {
  const quantities: CheckoutQuantities = {};
  let needsClampPersist = false;

  for (const lineItem of lineItems) {
    const clamped = clampQuantity(lineItem.id, lineItem.quantity);
    quantities[lineItem.id] = clamped > 0 ? String(clamped) : '0';
    if (clamped !== lineItem.quantity) {
      needsClampPersist = true;
    }
  }

  return { quantities, needsClampPersist };
}

/**
 * Identity of one clamp write-back attempt.
 *
 * The write-back re-seeds this effect through the updated order, so a backend that does not adopt
 * the clamped quantity would otherwise spin the pair into an endless update loop. Retrying only on
 * a changed signature bounds it to one attempt per distinct order state.
 */
export function checkoutClampPersistSignature(orderId: string, quantities: CheckoutQuantities): string {
  const entries = Object.keys(quantities)
    .sort()
    .map((key) => `${key}=${quantities[key]}`);
  return `${orderId}|${entries.join(',')}`;
}
